import { useCallback, useEffect, useRef, useState } from "react";

// Rozmowa głosowa z asystentką przez przeglądarkę (ten sam bridge co telefon, bez Asteriska).
// Mikrofon → PCM16 24k (worklet) → WS → bot; bot → PCM16 24k → odtwarzanie (pełny duplex, barge-in).
// Klient bez @types/react — czysty JS, walidacja przez `vite build`.

const WORKLET_URL = "/panel-call-capture-worklet.js";
const PLAYBACK_LEAD_SEC = 0.15;
const OUTPUT_RATE = 24000;

export function usePanelCall() {
  const [status, setStatus] = useState("idle"); // idle|connecting|live|ended|error
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState([]); // {speaker, text}[]
  const [muted, setMuted] = useState(false);

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const captureNodeRef = useRef(null);
  const mutedRef = useRef(false);
  const playheadRef = useRef(0);
  const sourcesRef = useRef(new Set());
  const asstOpenRef = useRef(false);

  const clearPlayback = useCallback(() => {
    for (const s of sourcesRef.current) {
      try {
        s.stop();
      } catch {
        /* ignore */
      }
    }
    sourcesRef.current.clear();
    playheadRef.current = 0;
  }, []);

  const cleanupAudio = useCallback(() => {
    const node = captureNodeRef.current;
    if (node) {
      try {
        node.port.onmessage = null;
      } catch {
        /* ignore */
      }
      try {
        node.disconnect();
      } catch {
        /* ignore */
      }
    }
    captureNodeRef.current = null;
    const stream = streamRef.current;
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    }
    streamRef.current = null;
    clearPlayback();
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx) {
      try {
        void ctx.close();
      } catch {
        /* ignore */
      }
    }
  }, [clearPlayback]);

  const playPcm24k = useCallback((int16) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const f32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) f32[i] = int16[i] / 0x8000;
    const buf = ctx.createBuffer(1, f32.length, OUTPUT_RATE);
    buf.copyToChannel(f32, 0);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    if (playheadRef.current < now + 0.02) playheadRef.current = now + PLAYBACK_LEAD_SEC;
    src.start(playheadRef.current);
    playheadRef.current += buf.duration;
    sourcesRef.current.add(src);
    src.onended = () => sourcesRef.current.delete(src);
  }, []);

  const appendLine = useCallback((speaker, text) => {
    if (!text || !text.trim()) return;
    setTranscript((prev) => {
      if (
        speaker === "assistant" &&
        asstOpenRef.current &&
        prev.length &&
        prev[prev.length - 1].speaker === "assistant"
      ) {
        const copy = prev.slice();
        copy[copy.length - 1] = { speaker, text };
        return copy;
      }
      if (speaker === "assistant") asstOpenRef.current = true;
      return prev.concat([{ speaker, text }]);
    });
  }, []);

  const handleControl = useCallback(
    (msg) => {
      if (!msg || typeof msg.type !== "string") return;
      switch (msg.type) {
        case "ready":
          setStatus("live");
          break;
        case "clear":
          clearPlayback();
          break;
        case "ended":
          cleanupAudio();
          setStatus((s) => (s === "error" ? s : "ended"));
          break;
        case "user_transcript":
          appendLine("user", msg.text);
          break;
        case "assistant_transcript":
          appendLine("assistant", msg.text);
          break;
        case "assistant_turn_complete":
        case "assistant_interrupted":
          asstOpenRef.current = false;
          break;
        default:
          break;
      }
    },
    [appendLine, cleanupAudio, clearPlayback],
  );

  const hangup = useCallback(() => {
    const ws = wsRef.current;
    if (ws) {
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "hangup" }));
      } catch {
        /* ignore */
      }
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    }
    wsRef.current = null;
    cleanupAudio();
    setStatus((s) => (s === "error" ? s : "ended"));
  }, [cleanupAudio]);

  // callerNumber: numer, którym panel ma się przedstawić. Pusty = rozmowa bez rozpoznawania,
  // dokładnie jak dotąd. trunkSlug: numer, którego zachowanie ma udawać test (powitanie, tryb,
  // doklejka). Pusty = wbudowane domyślne.
  const start = useCallback(async (callerNumber, trunkSlug) => {
    setError(null);
    setTranscript([]);
    asstOpenRef.current = false;
    setStatus("connecting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      const ctx = new AudioContext();
      ctxRef.current = ctx;
      if (ctx.state === "suspended") await ctx.resume();
      await ctx.audioWorklet.addModule(WORKLET_URL);

      const micSource = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, "panel-call-capture");
      captureNodeRef.current = node;
      micSource.connect(node);
      // Niektóre przeglądarki usypiają nieprzepięty worklet — spinamy przez wyciszony gain do destination.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      node.connect(sink);
      sink.connect(ctx.destination);

      const proto = location.protocol === "https:" ? "wss" : "ws";
      const caller = (callerNumber ?? "").trim();
      const trunk = (trunkSlug ?? "").trim();
      const params = new URLSearchParams();
      if (caller) params.set("caller", caller);
      if (trunk) params.set("trunk", trunk);
      const query = params.toString() ? `?${params.toString()}` : "";
      const ws = new WebSocket(`${proto}://${location.host}/admin/api/call/ws${query}`);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      node.port.onmessage = (e) => {
        if (mutedRef.current) return;
        if (ws.readyState === WebSocket.OPEN) ws.send(e.data);
      };

      ws.onmessage = (e) => {
        if (typeof e.data === "string") {
          try {
            handleControl(JSON.parse(e.data));
          } catch {
            /* ignore */
          }
          return;
        }
        playPcm24k(new Int16Array(e.data));
      };
      ws.onerror = () => setError("Błąd połączenia WebSocket.");
      ws.onclose = () => {
        cleanupAudio();
        setStatus((s) => (s === "error" ? s : "ended"));
      };
    } catch (err) {
      setError(String((err && err.message) || err || "Nie udało się rozpocząć rozmowy."));
      setStatus("error");
      cleanupAudio();
    }
  }, [cleanupAudio, handleControl, playPcm24k]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      mutedRef.current = !m;
      return !m;
    });
  }, []);

  // Sprzątanie przy odmontowaniu + zamknięcie karty (brak wiszących sesji Gemini).
  useEffect(() => {
    const onUnload = () => {
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      onUnload();
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return { status, error, transcript, muted, start, hangup, toggleMute };
}
